const positive = (value) => Number.isSafeInteger(value) && value > 0;
const code = (value, length) => typeof value === 'string' && new RegExp(`^[A-Z]{${length}}$`).test(value);
const idPattern = /^[1-9][0-9]*$/;
const fault = (name) => Object.assign(new Error(name), { code: name });

function safeSum(values) {
  const sum = values.reduce((total, value) => {
    if (!Number.isSafeInteger(value) || value < 0) throw fault('invalid_amount');
    return total + BigInt(value);
  }, 0n);
  if (sum > BigInt(Number.MAX_SAFE_INTEGER)) throw fault('invalid_amount');
  return Number(sum);
}

export function parseMoneyISO(money, expectedCurrency, amountDivisor = 100) {
  if (!code(expectedCurrency, 3) || money?.currencyCode !== expectedCurrency || !positive(amountDivisor)) throw fault('currency_mismatch');
  if (typeof money.amount !== 'string' || money.amount.length > 100 || !/^(0|[1-9][0-9]*)(\.[0-9]+)?$/.test(money.amount)) throw fault('invalid_amount');
  const [whole, fraction = ''] = money.amount.split('.');
  const digits = new Intl.NumberFormat('en', { style: 'currency', currency: expectedCurrency }).resolvedOptions().maximumFractionDigits;
  if (fraction.slice(digits).replace(/0/g, '')) throw fault('invalid_precision');
  const scale = 10n ** BigInt(fraction.length);
  const numerator = (BigInt(whole) * scale + BigInt(fraction || '0')) * BigInt(amountDivisor);
  if (numerator % scale !== 0n || numerator / scale > BigInt(Number.MAX_SAFE_INTEGER)) throw fault('invalid_amount');
  return Number(numerator / scale);
}

export function buildQuoteLines(quantities, catalog) {
  if (!quantities || typeof quantities !== 'object' || Array.isArray(quantities) || !Array.isArray(catalog)) throw fault('invalid_selection');
  const lines = Object.entries(quantities).map(([id, quantity]) => {
    const color = catalog.find((item) => String(item.id) === id);
    if (!idPattern.test(id) || !positive(quantity) || quantity > 2147483647 || !color) throw fault('invalid_selection');
    if (color.available === false) throw fault('stock');
    if (color.stock !== null && color.stock !== undefined && color.stock !== '') {
      if (!['number', 'string'].includes(typeof color.stock)) throw fault('stock');
      const stock = Number(color.stock);
      if (!Number.isFinite(stock) || stock < quantity) throw fault('stock');
    }
    return { merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity };
  }).sort((a, b) => a.merchandiseId.localeCompare(b.merchandiseId));
  if (!lines.length || lines.length > 250 || safeSum(lines.map((line) => line.quantity)) > 2147483647) throw fault('invalid_selection');
  return lines;
}

export function quoteSignature(quantities, country, currency) {
  return JSON.stringify([country, currency, Object.entries(quantities || {}).sort(([a], [b]) => a.localeCompare(b))]);
}

export function representativeSelections(catalog) {
  const available = catalog.filter((item) => item.available !== false && Number.isSafeInteger(item.price) && item.price >= 0 && (item.stock === null || item.stock === undefined || item.stock === '' || ['number', 'string'].includes(typeof item.stock) && Number.isFinite(Number(item.stock)) && Number(item.stock) >= 1)).sort((a, b) => a.price - b.price || String(a.id).localeCompare(String(b.id)));
  if (!available.length) throw fault('stock');
  const selections = {};
  for (let quantity = 1; quantity <= 5; quantity += 1) {
    let remaining = quantity;
    const selected = {};
    for (const item of available) {
      const stock = item.stock === null || item.stock === undefined || item.stock === '' ? remaining : Math.max(0, Math.floor(Number(item.stock)));
      const take = Math.min(remaining, stock);
      if (take > 0) selected[String(item.id)] = take;
      remaining -= take;
      if (!remaining) break;
    }
    selections[quantity] = remaining ? null : selected;
  }
  return { selections, markFrom: new Set(available.map((item) => item.price)).size > 1 };
}

const cartFields = (limit) => `
  buyerIdentity { countryCode }
  totalQuantity
  cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } subtotalAmountEstimated totalAmountEstimated }
  lines(first: ${limit}) {
    pageInfo { hasNextPage }
    nodes {
      quantity
      merchandise { ... on ProductVariant { id } }
      cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
      discountAllocations(lineLevelOnly: false) { targetType discountedAmount { amount currencyCode } }
    }
  }`;

export function buildQuoteOperation(requests, country) {
  if (!code(country, 2) || !requests.length || requests.length > 6) throw fault('invalid_context');
  const definitions = requests.map(({ alias }, index) => {
    if (!/^(selection|offer[1-5])$/.test(alias)) throw fault('invalid_request');
    return `$input${index}: CartInput!`;
  });
  if (new Set(requests.map((request) => request.alias)).size !== requests.length) throw fault('invalid_request');
  const variables = {};
  const fields = requests.map(({ alias, lines }, index) => {
    if (!lines?.length || lines.length > 250) throw fault('invalid_request');
    variables[`input${index}`] = { buyerIdentity: { countryCode: country }, lines };
    return `${alias}: cartCreate(input: $input${index}) { cart { ${cartFields(lines.length)} } userErrors { code field message } warnings { code message } }`;
  });
  return { query: `mutation LupsSelectionQuote(${definitions.join(', ')}) { ${fields.join('\n')} }`, variables };
}

export function readQuotePayload(payload, requestedLines, country, currency, amountDivisor = 100) {
  if (payload?.userErrors?.length) throw fault('quote_rejected');
  if (payload?.warnings?.length) throw fault('quote_warning');
  const cart = payload?.cart;
  if (!cart || cart.buyerIdentity?.countryCode !== country || !Array.isArray(cart.lines?.nodes) || cart.lines.pageInfo?.hasNextPage !== false) throw fault('context_mismatch');
  const expected = new Map(requestedLines.map((line) => [line.merchandiseId, line.quantity]));
  const actual = new Map();
  const totals = [];
  const retail = [];
  for (const line of cart.lines.nodes) {
    const id = line.merchandise?.id;
    if (!expected.has(id) || !positive(line.quantity)) throw fault('selection_mismatch');
    actual.set(id, safeSum([actual.get(id) || 0, line.quantity]));
    const subtotal = parseMoneyISO(line.cost?.subtotalAmount, currency, amountDivisor);
    parseMoneyISO(line.cost?.totalAmount, currency, amountDivisor);
    if (!Array.isArray(line.discountAllocations)) throw fault('invalid_response');
    const discounts = line.discountAllocations.map((allocation) => {
      const value = parseMoneyISO(allocation.discountedAmount, currency, amountDivisor);
      if (!['LINE_ITEM', 'SHIPPING_LINE'].includes(allocation.targetType)) throw fault('invalid_response');
      return allocation.targetType === 'LINE_ITEM' ? value : 0;
    });
    const discount = safeSum(discounts);
    if (discount > subtotal) throw fault('invalid_amount');
    retail.push(subtotal);
    totals.push(subtotal - discount);
  }
  if (actual.size !== expected.size || [...expected].some(([id, quantity]) => actual.get(id) !== quantity) || cart.totalQuantity !== safeSum(requestedLines.map((line) => line.quantity))) throw fault('selection_mismatch');
  const subtotal = parseMoneyISO(cart.cost?.subtotalAmount, currency, amountDivisor);
  const checkoutTotal = parseMoneyISO(cart.cost?.totalAmount, currency, amountDivisor);
  const retailBase = safeSum(retail);
  const total = safeSum(totals);
  return { total, subtotal, retailBase, savings: retailBase - total, checkoutTotal, country, currency, amountDivisor, source: 'shopify_storefront', estimated: true, isolated: true };
}

export function createQuoteApi({ endpoint = '/api/2026-07/graphql.json', origin, fetchImpl, amountDivisor = 100 }) {
  if (typeof fetchImpl !== 'function' || typeof origin !== 'string') throw fault('invalid_endpoint');
  const address = new URL(endpoint, origin);
  if (address.origin !== origin || address.pathname !== '/api/2026-07/graphql.json' || address.search || address.hash) throw fault('invalid_endpoint');
  const request = async (requests, country, currency, signal) => {
    if (!code(country, 2) || !code(currency, 3)) throw fault('invalid_context');
    const operation = buildQuoteOperation(requests, country);
    const response = await fetchImpl(address.href, { method: 'POST', credentials: 'omit', cache: 'no-store', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(operation), ...(signal ? { signal } : {}) });
    let result;
    try { result = await response.json(); } catch { throw fault('invalid_response'); }
    if (!response.ok || result?.errors?.length) throw fault('request_failed');
    return Object.fromEntries(requests.map(({ alias, lines }) => [alias, readQuotePayload(result?.data?.[alias], lines, country, currency, amountDivisor)]));
  };
  return {
    quote: async (quantities, catalog, country, currency, signal) => {
      const result = await request([{ alias: 'selection', lines: buildQuoteLines(quantities, catalog) }], country, currency, signal);
      return { ...result.selection, quantities: { ...quantities }, signature: quoteSignature(quantities, country, currency) };
    },
    offers: async (catalog, country, currency, signal) => {
      const { selections, markFrom } = representativeSelections(catalog);
      const requests = Object.entries(selections).filter(([, quantities]) => quantities).map(([quantity, quantities]) => ({ alias: `offer${quantity}`, lines: buildQuoteLines(quantities, catalog) }));
      const result = await request(requests, country, currency, signal);
      return { prices: Object.fromEntries(Object.keys(selections).map((quantity) => [quantity, result[`offer${quantity}`]?.total ?? null])), country, currency, representative: true, markFrom, selections, source: 'shopify_storefront', estimated: true, isolated: true };
    }
  };
}

const ElementBase = globalThis.HTMLElement || class {};

export class LupsSelectorQuote extends ElementBase {
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.hidden = true;
    this.sequence = 0;
    this.signature = null;
    this.selectionStatus = 'idle';
    this.offerStatus = 'idle';
    this.selector = this.closest('lups-color-selector');
    try { this.config = JSON.parse(this.querySelector('[data-selector-quote-config]')?.textContent || '{}'); } catch { this.config = {}; }
    if (this.config.enabled !== true || !this.selector) return;
    try {
      this.api = createQuoteApi({ endpoint: this.config.endpoint, origin: this.ownerDocument.location.origin, fetchImpl: globalThis.fetch.bind(globalThis), amountDivisor: 100 });
      if (!code(this.config.country, 2) || !code(this.config.currency, 3)) throw fault('invalid_context');
    } catch (error) { this.emit('lups:quote-result', { status: 'error', error: error.code || 'invalid_context' }); return; }
    this.selectionListener = (event) => this.schedule(event.detail || {});
    this.selector.addEventListener('lups:selection-change', this.selectionListener);
    this.retryButton = this.selector.querySelector('[data-quote-retry]');
    this.retryListener = () => this.retryQuotes();
    this.retryButton?.addEventListener('click', this.retryListener);
    queueMicrotask(() => {
      if (!this.connected) return;
      this.loadOffers();
      this.selector.renderSummary?.();
    });
  }

  disconnectedCallback() {
    this.connected = false;
    clearTimeout(this.timer);
    clearTimeout(this.timeout);
    this.controller?.abort();
    this.offerController?.abort();
    if (this.selectionListener) this.selector?.removeEventListener('lups:selection-change', this.selectionListener);
    if (this.retryListener) this.retryButton?.removeEventListener('click', this.retryListener);
    this.sequence += 1;
  }

  contextMatches() {
    return this.selector.market?.country === this.config.country && this.selector.market?.currency === this.config.currency;
  }

  emit(name, detail) {
    if (name === 'lups:quote-result') this.selectionStatus = detail.status;
    if (name === 'lups:quote-offers') this.offerStatus = detail.status;
    this.dispatchEvent(new CustomEvent(name, { detail: { country: this.config.country, currency: this.config.currency, total: null, ...detail }, bubbles: true }));
  }

  retryQuotes() {
    if (this.config.enabled !== true || !this.connected || !this.api) return;
    if (this.offerStatus === 'error') this.loadOffers();
    if (this.selectionStatus === 'error') {
      this.signature = null;
      this.schedule({ quantities: { ...this.selector.quantities } });
    }
  }

  schedule(detail) {
    const quantities = { ...detail.quantities };
    const signature = quoteSignature(quantities, this.selector.market?.country, this.selector.market?.currency);
    if (signature === this.signature) return;
    this.signature = signature;
    this.sequence += 1;
    const sequence = this.sequence;
    clearTimeout(this.timer);
    clearTimeout(this.timeout);
    this.controller?.abort();
    const context = { quantities, signature };
    if (!this.contextMatches()) { this.emit('lups:quote-result', { ...context, status: 'error', error: 'context_mismatch' }); return; }
    if (!Object.keys(quantities).length) { this.emit('lups:quote-result', { ...context, status: 'empty', total: 0 }); return; }
    this.emit('lups:quote-result', { ...context, status: 'busy' });
    this.timer = setTimeout(async () => {
      this.controller = new AbortController();
      this.timeout = setTimeout(() => this.controller.abort(), 12000);
      try {
        const result = await this.api.quote(quantities, this.selector.catalog, this.config.country, this.config.currency, this.controller.signal);
        if (sequence === this.sequence && this.connected && this.contextMatches()) this.emit('lups:quote-result', { ...result, status: 'ready' });
      } catch (error) {
        if (sequence === this.sequence && this.connected) this.emit('lups:quote-result', { ...context, status: 'error', error: error.code || 'request_failed' });
      } finally {
        if (sequence === this.sequence) clearTimeout(this.timeout);
      }
    }, 240);
  }

  async loadOffers() {
    if (!this.contextMatches()) return;
    this.offerController = new AbortController();
    const timeout = setTimeout(() => this.offerController.abort(), 12000);
    this.emit('lups:quote-offers', { status: 'busy' });
    try {
      const offers = await this.api.offers(this.selector.catalog, this.config.country, this.config.currency, this.offerController.signal);
      if (this.connected && this.contextMatches()) this.emit('lups:quote-offers', { ...offers, status: 'ready' });
    } catch (error) {
      if (this.connected) this.emit('lups:quote-offers', { status: 'error', error: error.code || 'request_failed' });
    } finally { clearTimeout(timeout); }
  }
}

if (globalThis.customElements && !globalThis.customElements.get('lups-selector-quote')) globalThis.customElements.define('lups-selector-quote', LupsSelectorQuote);
